import Table from "components/molecules/Table";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";
import { MouseEventHandler } from "react";
import { ArrowUpIcon, ArrowDownIcon, ArrowsUpDownIcon } from "@heroicons/react/20/solid";
import { activationKeyUserDescriptionMap } from "../../../constants";
import CirclePreview from "./CirclePreview/CirclePreview";
// import CirclePreview from "./CirclePreview/CirclePreview";

interface IProps {
  data: TopMementosData[];
  handleExport?: MouseEventHandler;
}

const columnHelper = createColumnHelper<TopMementosData>();

const columns = [
  columnHelper.accessor("name", {
    header: "Memento",
    cell: (info: any) => (
      <div className="flex max-w-[200x] items-center gap-3 pl-1.5">
        <CirclePreview
          className="shrink-0"
          name={info.row.original.name}
          imageURL={info.row.original.image_url}
          previewOf="Memento"
        />
        <div className="flex flex-col justify-start">
          <p className="text-ellipsis whitespace-nowrap">{info.row.original.name}</p>
          <p className="-mt-1 text-xxs opacity-70">
            {/* @ts-ignore */}
            {activationKeyUserDescriptionMap[info.row.original.activation_key]}
          </p>
        </div>
      </div>
    ),
    footer: (info: any) => info.column.id,
  }),
  columnHelper.accessor("magnet_name", {
    header: "Magnet",
    footer: (info: any) => info.column.id,
    cell: (info: any) => <div className="">{info.getValue()}</div>,
    size: 200,
  }),
  columnHelper.accessor("claimed_count", {
    header: () => <div className="inline-block">Total Interactions</div>,
    footer: (info: any) => info.column.id,
    cell: (info: any) => <div className="pr-8 text-center">{info.getValue()}</div>,
  }),
  columnHelper.accessor("dropped_count", {
    header: "Total Dropped",
    footer: (info: any) => info.column.id,
    cell: (info: any) => <div className="pr-8 text-center">{info.getValue()}</div>,
  }),
];

function TopMementosTable({ data }: IProps) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="secondary-scrollbar mr-12 max-h-[80vh] max-w-full overflow-y-scroll rounded-2xl border border-secondary bg-primary text-primary">
      <Table>
        <Table.Header>
          {table.getHeaderGroups().map((headerGroup: any) => (
            <Table.Row isHeaderRow key={`audience_table_rows${headerGroup.id}`} headerRowClasses="sticky top-0 z-10">
              {headerGroup.headers.map((header: any) => (
                <Table.HeaderCell
                  align="left"
                  label={
                    header.isPlaceholder ? null : (
                      <button
                        type="button"
                        {...{
                          className: header.column.getCanSort() ? "cursor-pointer select-none" : "",
                          onClick: header.column.getToggleSortingHandler(),
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <ArrowUpIcon className="ml-1 inline-block h-4 w-4" />,
                          desc: <ArrowDownIcon className="ml-1 inline-block h-4 w-4" />,
                          false: <ArrowsUpDownIcon className="ml-1 inline-block h-5 w-5" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </button>
                    )
                  }
                  key={`audience_table_header_${header.id}`}
                />
              ))}
            </Table.Row>
          ))}
        </Table.Header>
        <Table.Body className="font-satoshi text-sm font-semibold text-white">
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Table.Description textAlign="start" key={`audience_table_cell_${cell.id}`}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Table.Description>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

export default TopMementosTable;
